import type { CSSProperties } from 'react';
import { Alert, Input, Select } from 'antd';
import { type ShippingOrderLabel } from '@/constants/shippingCarriers';
import { orderLabelOptions, normalizeOrderLabel } from './orderLabelOptions';
import { CarrierList } from './CarrierList';
import { type PackageInfoValue } from './PackageInfoFields';
import { InsuranceField, type InsuranceValue } from './InsuranceField';
import type { CarrierFeeState } from '../../hooks/useCarrierFeeEstimates';
import type { PickupLocation } from '@/services/shipping.service';

export interface CarrierPanelValue {
  packageInfo: PackageInfoValue;
  note: string;
  orderLabel?: ShippingOrderLabel;
  promoCode: string;
  partner?: string;
  insurance: InsuranceValue;
  /** Nơi lấy hàng — hãng tới đây gom kiện, và phí tính từ đúng địa chỉ này. */
  pickupLocationId?: string;
}

interface Props {
  value: CarrierPanelValue;
  onChange: (v: CarrierPanelValue) => void;
  /** Bật sau khi bấm tạo đơn — truyền xuống các ô bắt buộc. */
  showErrors?: boolean;
  /** Phí hãng vừa báo, khoá theo `code` của hãng. Truyền thẳng xuống `CarrierList`. */
  feeByCarrier?: Map<string, CarrierFeeState>;
  /** Nút làm mới kiêm luôn việc tính lại phí. Truyền thẳng xuống `CarrierList`. */
  onReloadFees?: () => void;
  /** Nơi lấy hàng của CHI NHÁNH đang bán — nơi gọi đã lọc sẵn. */
  pickupLocations?: PickupLocation[];
  /** Đang tải danh sách nơi lấy hàng. */
  pickupLoading?: boolean;
  /**
   * Chỉ xem — `Orders/edit.tsx` render đúng panel này cho đơn đã chốt giỏ,
   * cùng bố cục với màn tạo đơn, chỉ khoá tương tác. Không dựng cách trình bày
   * thứ hai cho cùng một nhóm dữ liệu.
   */
  disabled?: boolean;
}

const LABEL: CSSProperties = { fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 4 };

const ALERT_STYLE: CSSProperties = { marginTop: 8 };

/**
 * Cỡ chữ cảnh báo bám đúng nhãn mục ("Ghi chú vận chuyển", "Nhãn đơn hàng"…).
 *
 * PHẢI đặt lên chính nội dung `message`, không đặt lên `style` của `Alert`:
 * antd tự cho `.ant-alert-message` một cỡ riêng (còn to hơn nữa khi có
 * `description`), nên style ở thẻ ngoài bị ghi đè và cảnh báo đọc như một khối
 * lạc ra từ trang khác. Lấy thẳng `LABEL.fontSize` thay vì gõ lại số.
 */
const ALERT_TEXT: CSSProperties = { fontSize: LABEL.fontSize };

/**
 * Cột giữa màn POS khi chọn "Giao cho ĐVVC": mọi thứ gửi cho đơn vị vận chuyển
 * gom về một chỗ.
 *
 * `packageInfo` vẫn thuộc `CarrierPanelValue` nhưng KHÔNG render ở đây —
 * `PackageInfoFields` được `POSPage` render ở đáy cột hàng, nơi thu ngân vừa nhìn
 * dòng hàng vừa cân gói. Giữ nó trong value để nơi gọi chỉ phải quản một object.
 */
/**
 * Nhãn một nơi lấy hàng: `Tên - Địa chỉ`.
 *
 * Chỉ mình cái tên thì hai kho đặt tên na ná nhau ("Kho 1", "Kho 2") không phân
 * biệt nổi; chỉ mình địa chỉ thì dài và không khớp với tên người dùng đã đặt ở
 * màn Nơi lấy hàng. Ghép cả hai.
 *
 * BỎ tiền tố tên chi nhánh: màn Nơi lấy hàng gợi ý tên theo dạng
 * `{Chi nhánh} - Nơi lấy hàng {n}`, mà danh sách ở đây ĐÃ lọc theo đúng chi
 * nhánh đang bán — giữ lại thì mọi dòng đều mở đầu bằng cùng một chuỗi thừa
 * ("CN1 - Nơi lấy hàng 1 - 12 Lê Lợi"), đẩy phần thật sự phân biệt ra xa.
 * Chỉ cắt khi tên ĐÚNG là bắt đầu bằng tên chi nhánh, nên tên tự đặt giữ nguyên.
 *
 * Thiếu vế nào bỏ vế đó — KHÔNG để lại dấu gạch cụt. Bản ghi cũ có thể chưa có
 * `name` (trường này thêm sau), và bản ghi thiếu `address` thì rơi về người liên
 * hệ để ô chọn không bao giờ hiện dòng trống.
 */
export function pickupLabel(location: PickupLocation): string {
  const storeName =
    typeof location.store_id === 'object' ? location.store_id?.name?.trim() : undefined;
  let name = location.name?.trim();
  if (name && storeName && name.startsWith(`${storeName} - `)) {
    name = name.slice(storeName.length + 3).trim();
  }

  const parts = [name, location.address].filter(
    (part): part is string => Boolean(part?.trim()),
  );
  return parts.length > 0 ? parts.join(' - ') : location.contact_name;
}

export function CarrierPanel({
  value,
  onChange,
  showErrors,
  feeByCarrier,
  onReloadFees,
  pickupLocations,
  pickupLoading,
  disabled,
}: Props): JSX.Element {
  const patch = (part: Partial<CarrierPanelValue>) => onChange({ ...value, ...part });

  const locations = pickupLocations ?? [];
  const branchHasNoPickup = !pickupLoading && locations.length === 0;
  const missingPickup = !pickupLoading && locations.length > 0 && !value.pickupLocationId;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={LABEL}>Ghi chú vận chuyển</div>
        <Input.TextArea
          aria-label="Ghi chú vận chuyển"
          rows={3}
          disabled={disabled}
          placeholder="Nhập ghi chú"
          value={value.note}
          onChange={(e) => patch({ note: e.target.value })}
        />
      </div>

      <div>
        <div style={LABEL}>Nhãn đơn hàng</div>
        <Select
          aria-label="Nhãn đơn hàng"
          allowClear
          style={{ width: '100%' }}
          disabled={disabled || !value.partner}
          placeholder={value.partner ? 'Chọn nhãn' : 'Chọn đơn vị vận chuyển trước'}
          value={value.orderLabel}
          onChange={(orderLabel) => patch({ orderLabel })}
          options={orderLabelOptions(value.partner)}
        />
      </div>

      <InsuranceField
        value={value.insurance}
        onChange={(insurance) => patch({ insurance })}
        hasError={showErrors}
        disabled={disabled}
      />

      <div>
        <div style={LABEL}>Mã khuyến mại</div>
        <Input
          aria-label="Mã khuyến mại"
          disabled={disabled}
          placeholder="Áp dụng cho ĐVVC"
          value={value.promoCode}
          onChange={(e) => patch({ promoCode: e.target.value })}
        />
      </div>

      {/*
        Nơi lấy hàng đặt NGAY DƯỚI mã khuyến mại và NGAY TRÊN danh sách hãng:
        đây là thứ quyết định phí ở dòng dưới, nên phải nằm trước nó theo hướng
        đọc, không phải nhét xuống cuối form.
      */}
      <div>
        <div style={LABEL}>Nơi lấy hàng</div>
        <Select
          aria-label="Nơi lấy hàng"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          loading={pickupLoading}
          disabled={disabled || branchHasNoPickup}
          status={missingPickup ? 'warning' : undefined}
          placeholder={
            branchHasNoPickup ? 'Chi nhánh chưa có địa chỉ gửi hàng' : 'Chọn nơi lấy hàng'
          }
          value={value.pickupLocationId}
          onChange={(pickupLocationId) => patch({ pickupLocationId })}
          options={locations.map((location) => ({
            value: location._id,
            label: pickupLabel(location),
          }))}
        />
        {branchHasNoPickup && (
          <Alert
            type="warning"
            showIcon
            style={ALERT_STYLE}
            message={
              <span style={ALERT_TEXT}>
                Chưa cài đặt nơi lấy hàng cho chi nhánh này,{' '}
                {/*
                  Thẻ `a` thường, KHÔNG phải `Link` của router: mở tab MỚI để thu
                  ngân không mất giỏ hàng đang dở. Điều hướng trong tab hiện tại
                  là vứt cả đơn đang bán.
                */}
                <a
                  href="/shipping/pickup-locations"
                  target="_blank"
                  rel="noreferrer"
                >
                  ấn vào đây
                </a>{' '}
                để thiết lập chi nhánh
              </span>
            }
          />
        )}
        {missingPickup && (
          <Alert
            type="warning"
            showIcon
            style={ALERT_STYLE}
            message={
              <span style={ALERT_TEXT}>Chọn nơi lấy hàng để tính phí vận chuyển</span>
            }
          />
        )}
      </div>

      <CarrierList
        value={value.partner}
        onChange={(partner) =>
          onChange({
            ...value,
            partner,
            orderLabel: normalizeOrderLabel(value.orderLabel, partner),
          })
        }
        hasError={showErrors}
        feeByCarrier={feeByCarrier}
        onReloadFees={onReloadFees}
        disabled={disabled}
      />
    </div>
  );
}
